import type { JumpPhase } from '../../core/jump/jump';

/**
 * What the scene shows of El Salto (GDD §11.1, §11.3, §3.4, issue #94), as a pure function of the state of the core:
 * the border sign from the crossing, the X from the jump zone, and the pilot, its parachute and the empty plane from
 * the jump. Nothing here knows Three.js: the scene only applies it.
 */
export interface JumpViewInput {
  /** The US sky has started (the border was crossed). */
  usSky: boolean;
  /** The jump zone is active: the view is frozen and the X is on the ground. */
  zone: boolean;
  jump: JumpPhase | null;
  /** The empty plane after the jump, and the right edge of the frozen view. */
  planeX: number | null;
  viewRight: number;
}

export interface JumpVisibility {
  borderSign: boolean;
  target: boolean;
  /** The plane (with its pilot before the jump, empty after it) until it has left the screen. */
  plane: boolean;
  pilotFalling: boolean;
  parachute: boolean;
  pilotLanded: boolean;
}

/** The empty plane is gone once its tail is this far past the right edge of the view (u). */
export const PLANE_EXIT_MARGIN = 1.5;

export function jumpVisibility(input: JumpViewInput): JumpVisibility {
  const plane = input.jump === null || (input.planeX !== null && input.planeX < input.viewRight + PLANE_EXIT_MARGIN);
  return {
    borderSign: input.usSky,
    target: input.zone,
    plane,
    pilotFalling: input.jump === 'falling' || input.jump === 'parachute',
    parachute: input.jump === 'parachute',
    pilotLanded: input.jump === 'landed',
  };
}

/**
 * The empty plane pitches comically as it flies on, and never smokes or burns (GDD §3.4): a slow nose bob that grows
 * a little with time. In degrees; steady with reduced motion.
 */
export function emptyPlanePitch(ticksSinceJump: number, reducedMotion: boolean): number {
  if (reducedMotion) return 0;
  return Math.sin(ticksSinceJump * 0.12) * 14;
}
